import type { TabProps, TabsState, TabsStorageContextProps } from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { LayoutPanelApi } from '../../../types';

/**
 * Build a TabsStorage implementation backed by dockview panel params.
 */
export function usePanelTabsStorage(
  panelTabs: TabsState<TabProps> | undefined,
  panelApi: LayoutPanelApi
): TabsStorageContextProps {
  const panelTabsRef = useRef<TabsState<TabProps> | undefined>(panelTabs);
  const itemsRef = useRef<TabProps[] | undefined>(panelTabs?.items);
  const selectedIndexRef = useRef<number | undefined>(panelTabs?.selectedIndex);

  useEffect(() => {
    panelTabsRef.current = panelTabs;
    if (itemsRef.current == null && panelTabs?.items != null) {
      itemsRef.current = panelTabs.items;
    }
    if (selectedIndexRef.current == null && panelTabs?.selectedIndex != null) {
      selectedIndexRef.current = panelTabs.selectedIndex;
    }
  }, [panelTabs]);

  const getState = useCallback<TabsStorageContextProps['getState']>(tabsID => {
    if (tabsID == null || panelTabsRef.current == null) {
      return null;
    }

    return panelTabsRef.current;
  }, []);

  const setSelectedIndex = useCallback<TabsStorageContextProps['setSelectedIndex']>(
    (tabsID, index) => {
      if (tabsID == null) {
        return;
      }

      selectedIndexRef.current = index;

      const items = itemsRef.current;
      panelApi.updateParameters({
        tabs: {
          ...(items == null ? {} : { items }),
          selectedIndex: index,
        },
      });
    },
    [panelApi]
  );

  const setItems = useCallback<TabsStorageContextProps['setItems']>(
    (tabsID, items) => {
      if (tabsID == null) {
        return;
      }

      itemsRef.current = items;

      const selectedIndex = selectedIndexRef.current;
      panelApi.updateParameters({
        tabs: {
          items,
          ...(selectedIndex == null ? {} : { selectedIndex }),
        },
      });
    },
    [panelApi]
  );

  return useMemo(
    () => ({
      getState,
      setSelectedIndex,
      setItems,
    }),
    [getState, setItems, setSelectedIndex]
  );
}
